import { format } from 'date-fns'
import { forwardRef } from 'react'
import {
  FALLBACK_SEGMENT_COLOR,
  sortedBreakdownEntries,
} from '@opallabs/calendar'
import { kuiperClassNames } from '@opallabs/kuiper'

type HistogramBarProps = {
  height: number
  totalCount: number
  breakdown?: Record<string, number>
  colorFor?: (groupId: string) => string
  /** When false (Time/None sorts, non-active weeks) the bar stays solid. */
  shouldStack?: boolean
}

export const formatDayHeading = (date: Date): string =>
  format(date, 'EEE, MMM d')

export const formatWeekHeading = (firstDay: Date, lastDay: Date): string => {
  const sameMonth = format(firstDay, 'yyyy-MM') === format(lastDay, 'yyyy-MM')
  const start = format(firstDay, 'MMM d')
  const end = sameMonth ? format(lastDay, 'd') : format(lastDay, 'MMM d')
  return `Week of ${start}–${end}`
}

// forwardRef + rest-props pass-through so Lapidary's Tooltip (Radix
// `asChild`) can attach its ref and pointer handlers to the span.
//
// `breakdown` is expected at the bar's display granularity: for Account
// sort that's already aggregated by parent service (so this bar paints
// the same as Channel sort), for other sorts it's the leaf-level map.
export const HistogramBar = forwardRef<HTMLSpanElement, HistogramBarProps>(
  ({ height, totalCount, breakdown, colorFor, shouldStack, ...rest }, ref) => {
    const hasSegments =
      shouldStack === true &&
      breakdown != null &&
      Object.keys(breakdown).length > 0 &&
      totalCount > 0
    const segments = hasSegments ? sortedBreakdownEntries(breakdown!) : []
    const segmentTotal = segments.reduce((sum, [, count]) => sum + count, 0)
    return (
      <span
        className={kuiperClassNames('bar')}
        ref={ref}
        style={{ height }}
        {...rest}
      >
        {hasSegments &&
          segments.map(([groupId, count]) => {
            const color = colorFor?.(groupId) ?? FALLBACK_SEGMENT_COLOR
            const segmentPercent = (count / segmentTotal) * 100
            return (
              <span
                key={groupId}
                className={kuiperClassNames('bar-segment')}
                style={{
                  backgroundColor: color,
                  flexBasis: `${segmentPercent}%`,
                }}
              />
            )
          })}
      </span>
    )
  }
)
HistogramBar.displayName = 'HistogramBar'
